import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
    Box,
    Typography,
    Breadcrumbs,
    Link,
    IconButton,
    Tooltip,
    Skeleton,
    useTheme,
} from "@mui/material";
import { AltArrowLeft, Download } from "@solar-icons/react/ssr";

import { request } from "@/Api";
import { API_BASE_URL } from "@/config";
import { downloadFile } from "@/utils";
import MarkdownRichEditor from "@/common/MarkdownRichEditor";
import AiLogo from "@/ai/AiLogo";

/**
 * CanvasPage — standalone page for viewing/editing a single canvas.
 *
 * Route: /canvas/:canvasId
 *
 * Fetches the canvas data, then resolves the parent thread
 * to get project context for breadcrumbs and navigation.
 */
export default function CanvasPage() {
    const { canvasId } = useParams();
    const navigate = useNavigate();
    const theme = useTheme();

    const [canvas, setCanvas] = useState(null);
    const [thread, setThread] = useState(null);
    const [project, setProject] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const canvasRef = useRef(null);

    // Chain: canvas → thread → project
    useEffect(() => {
        if (!canvasId) return;
        setLoading(true);
        setError(null);

        request
            .get(`/canvas/${canvasId}`)
            .then((canvasRes) => {
                const canvasData = canvasRes.data;
                setCanvas(canvasData);
                canvasRef.current = canvasData;
                return request.get(`/threads/${canvasData.thread_id}`);
            })
            .then((threadRes) => {
                const threadData = threadRes.data;
                setThread(threadData);
                if (
                    threadData.model_type === "project" &&
                    threadData.model_instance_id
                ) {
                    return request.get(
                        `/projects/${threadData.model_instance_id}`,
                    );
                }
                return null;
            })
            .then((projectRes) => {
                if (projectRes) {
                    setProject(projectRes.data);
                }
            })
            .catch((err) => {
                setError(
                    err?.response?.status === 404
                        ? "Canvas not found"
                        : "Failed to load canvas",
                );
            })
            .finally(() => setLoading(false));
    }, [canvasId]);

    const handleChange = (markdown) => {
        canvasRef.current = { ...canvasRef.current, content: markdown };
    };

    const handleSave = () => {
        if (!canvasRef.current || !canvasId) return;
        request.patch(`/canvas/${canvasId}`, canvasRef.current);
    };

    const handleExport = () => {
        downloadFile(`${API_BASE_URL}/brief-export/${canvasId}`);
    };

    if (loading) {
        return <CanvasPageSkeleton />;
    }

    if (error) {
        return (
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    height: "60vh",
                }}
            >
                <Typography color="error">{error}</Typography>
            </Box>
        );
    }

    return (
        <Box
            sx={{
                display: "flex",
                flexDirection: "column",
                height: "calc(100vh - 64px)",
                overflow: "hidden",
            }}
        >
            {/* Header */}
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    px: 3,
                    py: 1.5,
                    borderBottom: `1px solid ${theme.palette.divider}`,
                    flexShrink: 0,
                }}
            >
                <Tooltip title="Back to project">
                    <IconButton
                        size="small"
                        onClick={() => {
                            if (project) {
                                navigate(`/projects/${project.id}`);
                            } else {
                                navigate(-1);
                            }
                        }}
                    >
                        <AltArrowLeft size={20} />
                    </IconButton>
                </Tooltip>

                <AiLogo additionalStyles={{ width: "18px", height: "18px" }} />

                <Breadcrumbs separator="›" sx={{ fontSize: 13 }}>
                    {project && (
                        <Link
                            underline="hover"
                            color="text.secondary"
                            href={`/projects/${project.id}`}
                            onClick={(e) => {
                                e.preventDefault();
                                navigate(`/projects/${project.id}`);
                            }}
                            sx={{ fontSize: 13 }}
                        >
                            {project.name}
                        </Link>
                    )}
                    <Typography
                        color="text.primary"
                        sx={{ fontSize: 13, fontWeight: 600 }}
                    >
                        {canvas?.name || "Canvas"}
                    </Typography>
                </Breadcrumbs>

                <Box sx={{ flex: 1 }} />

                <Tooltip title="Export as DOCX">
                    <IconButton size="small" onClick={handleExport}>
                        <Download size={20} />
                    </IconButton>
                </Tooltip>
            </Box>

            {/* Editor */}
            <Box sx={{ flex: 1, overflow: "auto", px: 3, py: 2 }}>
                {canvas && (
                    <Box sx={{ maxWidth: 900, mx: "auto" }}>
                        <MarkdownRichEditor
                            injectedMarkdown={canvas.content}
                            onBlur={handleSave}
                            onChange={handleChange}
                        />
                    </Box>
                )}
            </Box>
        </Box>
    );
}

function CanvasPageSkeleton() {
    return (
        <Box sx={{ px: 3, py: 3, maxWidth: 900, mx: "auto" }}>
            <Skeleton variant="text" width={200} height={24} sx={{ mb: 2 }} />
            <Skeleton
                variant="rectangular"
                width="100%"
                height={80}
                sx={{ borderRadius: 2, mb: 2 }}
            />
            {Array.from({ length: 12 }).map((_, i) => (
                <Skeleton
                    key={i}
                    variant="text"
                    width={`${70 + Math.random() * 30}%`}
                    height={18}
                    sx={{ mb: 0.5 }}
                />
            ))}
        </Box>
    );
}
